// ---------------------------------------------------------------------------
// DSS-31: Flex controls — visual flexbox layout editor
// ---------------------------------------------------------------------------
import { useCallback, useId } from "react";
import { sanitizeCssValue } from "../style-state-system";
import {
  ArrowRight,
  ArrowLeft,
  ArrowDown,
  ArrowUp,
  WrapText,
  AlignHorizontalJustifyStart,
  AlignHorizontalJustifyCenter,
  AlignHorizontalJustifyEnd,
  AlignHorizontalSpaceBetween,
  AlignHorizontalSpaceAround,
  AlignVerticalJustifyStart,
  AlignVerticalJustifyCenter,
  AlignVerticalJustifyEnd,
  AlignVerticalSpaceBetween,
  AlignVerticalSpaceAround,
  Columns3,
  StretchHorizontal,
  Baseline,
} from "lucide-react";
import type { ReactNode } from "react";

// ── Types ──
export type FlexDirection = "row" | "row-reverse" | "column" | "column-reverse";
export type FlexWrap = "nowrap" | "wrap" | "wrap-reverse";
export type JustifyContent =
  | "flex-start"
  | "center"
  | "flex-end"
  | "space-between"
  | "space-around"
  | "space-evenly";
export type AlignItems = "flex-start" | "center" | "flex-end" | "stretch" | "baseline";
export type AlignContent =
  | "flex-start"
  | "center"
  | "flex-end"
  | "space-between"
  | "space-around"
  | "stretch";
export type GapUnit = "px" | "rem" | "%";

export interface GapValue {
  readonly value: number;
  readonly unit: GapUnit;
}

export interface FlexConfig {
  readonly direction: FlexDirection;
  readonly wrap: FlexWrap;
  readonly justifyContent: JustifyContent;
  readonly alignItems: AlignItems;
  readonly alignContent: AlignContent;
  readonly rowGap: GapValue;
  readonly columnGap: GapValue;
}

// ── Constants ──
const GAP_UNITS: readonly GapUnit[] = ["px", "rem", "%"];
const MIN_GAP = 0;
const MAX_GAP = 999;

interface OptionDef<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly icon: ReactNode;
}

const DIRECTION_OPTIONS: readonly OptionDef<FlexDirection>[] = [
  { value: "row", label: "Row", icon: <ArrowRight className="h-3.5 w-3.5" /> },
  { value: "row-reverse", label: "Row Rev", icon: <ArrowLeft className="h-3.5 w-3.5" /> },
  { value: "column", label: "Column", icon: <ArrowDown className="h-3.5 w-3.5" /> },
  { value: "column-reverse", label: "Col Rev", icon: <ArrowUp className="h-3.5 w-3.5" /> },
];

const WRAP_OPTIONS: readonly OptionDef<FlexWrap>[] = [
  { value: "nowrap", label: "No Wrap", icon: <ArrowRight className="h-3.5 w-3.5" /> },
  { value: "wrap", label: "Wrap", icon: <WrapText className="h-3.5 w-3.5" /> },
  { value: "wrap-reverse", label: "Wrap Rev", icon: <ArrowUp className="h-3.5 w-3.5" /> },
];

const JUSTIFY_OPTIONS: readonly OptionDef<JustifyContent>[] = [
  { value: "flex-start", label: "Start", icon: <AlignHorizontalJustifyStart className="h-3.5 w-3.5" /> },
  { value: "center", label: "Center", icon: <AlignHorizontalJustifyCenter className="h-3.5 w-3.5" /> },
  { value: "flex-end", label: "End", icon: <AlignHorizontalJustifyEnd className="h-3.5 w-3.5" /> },
  { value: "space-between", label: "Between", icon: <AlignHorizontalSpaceBetween className="h-3.5 w-3.5" /> },
  { value: "space-around", label: "Around", icon: <AlignHorizontalSpaceAround className="h-3.5 w-3.5" /> },
  { value: "space-evenly", label: "Evenly", icon: <Columns3 className="h-3.5 w-3.5" /> },
];

const ALIGN_ITEMS_OPTIONS: readonly OptionDef<AlignItems>[] = [
  { value: "flex-start", label: "Start", icon: <AlignVerticalJustifyStart className="h-3.5 w-3.5" /> },
  { value: "center", label: "Center", icon: <AlignVerticalJustifyCenter className="h-3.5 w-3.5" /> },
  { value: "flex-end", label: "End", icon: <AlignVerticalJustifyEnd className="h-3.5 w-3.5" /> },
  { value: "stretch", label: "Stretch", icon: <StretchHorizontal className="h-3.5 w-3.5" /> },
  { value: "baseline", label: "Baseline", icon: <Baseline className="h-3.5 w-3.5" /> },
];

const ALIGN_CONTENT_OPTIONS: readonly OptionDef<AlignContent>[] = [
  { value: "flex-start", label: "Start", icon: <AlignVerticalJustifyStart className="h-3.5 w-3.5" /> },
  { value: "center", label: "Center", icon: <AlignVerticalJustifyCenter className="h-3.5 w-3.5" /> },
  { value: "flex-end", label: "End", icon: <AlignVerticalJustifyEnd className="h-3.5 w-3.5" /> },
  { value: "space-between", label: "Between", icon: <AlignVerticalSpaceBetween className="h-3.5 w-3.5" /> },
  { value: "space-around", label: "Around", icon: <AlignVerticalSpaceAround className="h-3.5 w-3.5" /> },
  { value: "stretch", label: "Stretch", icon: <StretchHorizontal className="h-3.5 w-3.5" /> },
];

const DEFAULT_GAP: GapValue = { value: 0, unit: "px" };

const DEFAULT_CONFIG: FlexConfig = {
  direction: "row",
  wrap: "nowrap",
  justifyContent: "flex-start",
  alignItems: "stretch",
  alignContent: "stretch",
  rowGap: DEFAULT_GAP,
  columnGap: DEFAULT_GAP,
};

// ── NaN guard ──
function safeGap(val: number): number {
  const n = Number(val);
  if (Number.isNaN(n)) return 0;
  return Math.min(Math.max(n, MIN_GAP), MAX_GAP);
}

// ── CSS generator ──
export function generateFlexCss(config: FlexConfig): string {
  const lines: string[] = ["display: flex;"];

  if (config.direction !== "row") {
    lines.push(`flex-direction: ${sanitizeCssValue(config.direction)};`);
  }
  if (config.wrap !== "nowrap") {
    lines.push(`flex-wrap: ${sanitizeCssValue(config.wrap)};`);
  }
  if (config.justifyContent !== "flex-start") {
    lines.push(`justify-content: ${sanitizeCssValue(config.justifyContent)};`);
  }
  if (config.alignItems !== "stretch") {
    lines.push(`align-items: ${sanitizeCssValue(config.alignItems)};`);
  }
  if (config.alignContent !== "stretch" && config.wrap !== "nowrap") {
    lines.push(`align-content: ${sanitizeCssValue(config.alignContent)};`);
  }
  if (config.rowGap.value > 0) {
    lines.push(`row-gap: ${safeGap(config.rowGap.value)}${config.rowGap.unit};`);
  }
  if (config.columnGap.value > 0) {
    lines.push(`column-gap: ${safeGap(config.columnGap.value)}${config.columnGap.unit};`);
  }

  return lines.join("\n");
}

// ── Shared icon-button grid ──
function OptionGrid<T extends string>({
  options,
  value,
  onChange,
  label,
}: {
  readonly options: readonly OptionDef<T>[];
  readonly value: T;
  readonly onChange: (v: T) => void;
  readonly label: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[10px] font-medium text-[var(--color-muted-foreground)]">{label}</span>
      <div className="flex flex-wrap gap-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            title={opt.label}
            aria-label={`${label}: ${opt.label}`}
            aria-pressed={value === opt.value}
            onClick={() => onChange(opt.value)}
            className={[
              "flex items-center justify-center rounded-md border p-1.5 transition-colors",
              value === opt.value
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
            ].join(" ")}
          >
            {opt.icon}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Gap input ──
function GapInput({
  label,
  value,
  onChange,
  id,
}: {
  readonly label: string;
  readonly value: GapValue;
  readonly onChange: (v: GapValue) => void;
  readonly id: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-[10px] font-medium text-[var(--color-muted-foreground)]">
        {label}
      </label>
      <div className="flex gap-1">
        <input
          id={id}
          type="number"
          min={MIN_GAP}
          max={MAX_GAP}
          value={value.value}
          onChange={(e) => onChange({ ...value, value: safeGap(Number(e.target.value)) })}
          className="h-7 w-16 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-2 text-[11px] text-[var(--color-foreground)]"
          aria-label={label}
        />
        <select
          value={value.unit}
          onChange={(e) => onChange({ ...value, unit: e.target.value as GapUnit })}
          className="h-7 rounded border border-[var(--color-border)] bg-[var(--color-background)] px-1 text-[10px] text-[var(--color-foreground)]"
          aria-label={`${label} unit`}
        >
          {GAP_UNITS.map((u) => (
            <option key={u} value={u}>{u}</option>
          ))}
        </select>
      </div>
    </div>
  );
}

// ── Props ──
interface FlexControlsProps {
  readonly value: FlexConfig | undefined;
  readonly onChange: (config: FlexConfig) => void;
}

// ── Component ──
export default function FlexControls({ value, onChange }: FlexControlsProps) {
  const config = value ?? DEFAULT_CONFIG;
  const baseId = useId();

  const update = useCallback(
    <K extends keyof FlexConfig>(key: K, val: FlexConfig[K]) => {
      onChange({ ...config, [key]: val });
    },
    [config, onChange],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Columns3 className="h-3.5 w-3.5" />
        Flex Layout
      </div>

      {/* Direction */}
      <OptionGrid
        options={DIRECTION_OPTIONS}
        value={config.direction}
        onChange={(v) => update("direction", v)}
        label="Direction"
      />

      {/* Wrap */}
      <OptionGrid
        options={WRAP_OPTIONS}
        value={config.wrap}
        onChange={(v) => update("wrap", v)}
        label="Wrap"
      />

      {/* Justify Content */}
      <OptionGrid
        options={JUSTIFY_OPTIONS}
        value={config.justifyContent}
        onChange={(v) => update("justifyContent", v)}
        label="Justify content"
      />

      {/* Align Items */}
      <OptionGrid
        options={ALIGN_ITEMS_OPTIONS}
        value={config.alignItems}
        onChange={(v) => update("alignItems", v)}
        label="Align items"
      />

      {/* Align Content (only relevant when wrapping) */}
      {config.wrap !== "nowrap" && (
        <OptionGrid
          options={ALIGN_CONTENT_OPTIONS}
          value={config.alignContent}
          onChange={(v) => update("alignContent", v)}
          label="Align content"
        />
      )}

      {/* Gap controls */}
      <div className="flex gap-3">
        <GapInput
          label="Row gap"
          value={config.rowGap}
          onChange={(v) => update("rowGap", v)}
          id={`${baseId}-row-gap`}
        />
        <GapInput
          label="Col gap"
          value={config.columnGap}
          onChange={(v) => update("columnGap", v)}
          id={`${baseId}-col-gap`}
        />
      </div>

      {/* CSS preview */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="whitespace-pre-wrap text-[10px] text-[var(--color-muted-foreground)]">
          {generateFlexCss(config)}
        </code>
      </div>
    </div>
  );
}
